import { Autocomplete, FormControl, InputAdornment, TextField } from '@mui/material';
import { ConversationStatus } from '@provision/conversation-types';
import { UrlParams } from '@provision/portal-routes';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Control, Controller, ValidationRule } from 'react-hook-form';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { Conversation } from 'src/graphql';
import { useSearchConversations } from 'src/graphql/entities/search/search-conversation';
import { useSelectedConversation } from 'src/pages/Conversations';
import { useScrollPaginateHandler } from 'src/utils/hooks';
import { useRouteParams } from 'src/utils/routing';
import { ContactSupportFormValues } from './contact-support-modal';

interface SearchConversationFieldProps {
  control: Control<ContactSupportFormValues>;
  setValue: (conversation: Conversation) => void;
  requiredValidation: ValidationRule<boolean>;
}
// LinkConversationData is lightweight type that includes minimal required fields for this component.
// It is needed to avoid using Conversation type in conjunction with `useForm` as it fails to properly
// resolve types and TypeScript complains on "excessively deep and possibly infinite" type instantiation.
export type LinkConversationData = Pick<
  Conversation,
  'friendlyId' | 'subject' | 'conversationId' | 'partnerId'
>;

export const SearchConversationField = ({
  control,
  setValue,
  requiredValidation,
}: SearchConversationFieldProps) => {
  const [searchTerm, setSearchTerm] = useState('');

  const { recordId: conversationId } = useRouteParams(UrlParams.recordId);

  const { selectedConversation } = useSelectedConversation({
    conversationId: conversationId || '',
  });

  useEffect(() => {
    if (selectedConversation) {
      setValue(selectedConversation);
    }
  }, [setValue, selectedConversation]);

  const searchConversationsFilter = useMemo(
    () => [
      {
        attribute: 'conversationStatusId',
        operator: '!=',
        value: ConversationStatus.Closed,
      },
    ],
    [],
  );

  const { records, loadMore, isLoading, hasMore } = useSearchConversations({
    searchTerm,
    filter: searchConversationsFilter,
    sort: {
      attribute: 'subject',
    },
  });

  const onInputChange = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => setSearchTerm(target.value),
    [],
  );

  const handleScroll = useScrollPaginateHandler({
    loadMore,
    enabled: hasMore,
    thresholdPx: 200,
  });

  return (
    <FormControl fullWidth margin="normal">
      <Controller
        name="linkedConversation"
        control={control}
        defaultValue={null}
        rules={{
          required: requiredValidation,
        }}
        render={({ field, fieldState }) => (
          <Autocomplete<LinkConversationData>
            {...field}
            autoHighlight
            loading={isLoading}
            blurOnSelect
            fullWidth
            onChange={(_, value: LinkConversationData | null) => {
              field.onChange(value);
            }}
            popupIcon={null}
            renderInput={(params) => {
              return (
                <TextField
                  {...params}
                  placeholder="Search & Select Conversation"
                  autoFocus
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  onChange={onInputChange}
                  label="Linked Conversation"
                  inputProps={{
                    ...params.inputProps,
                    sx: {
                      cursor: 'pointer',
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <DynamicIcon name="search" />
                      </InputAdornment>
                    ),
                  }}
                />
              );
            }}
            options={records}
            getOptionLabel={(conversation) =>
              `${conversation.friendlyId} | ${conversation.subject}`
            }
            getOptionKey={(conversation) => conversation.conversationId}
            isOptionEqualToValue={(option, value) =>
              option.conversationId === value.conversationId
            }
            ListboxProps={{
              onScroll: handleScroll,
            }}
          />
        )}
      />
    </FormControl>
  );
};
